import { useDispatch, useSelector } from 'react-redux';

// Types
import { User } from 'Shared/constants/types';
import { InvitationSource, InvalidAssessmentId } from 'Shared/types/BaInfo.types';

//Hooks
import { useOrganization } from './use-organization';
import { useApp } from './use-app';
import { useToggle } from 'Shared/utils/hooks';
import { useBaProfile } from './use-ba-profile';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Actions
import { createBehavioralAssessment } from 'Shared/actions/PiIntegrations.actions';

//Selectors
import { getPendingAssessmentIdByUserSlug } from 'Shared/selectors/BaInfo.selectors';

type Props = {
  user: User;
  isTakingBaForSelf: boolean;
};

export const useCreateOrSendBa = ({ user, isTakingBaForSelf }: Props) => {
  const { organization } = useOrganization();
  const { app } = useApp();
  const { baProfile } = useBaProfile({ user });
  const [isCreatingBaLink, toggleIsCreatingBaLink] = useToggle(false);
  const dispatch = useDispatch();
  const { slug: currentUserSlug } = useCurrentUser();
  const { user: loggedInUser } = useUser({ slug: currentUserSlug });

  // If we had generated a BA link already in the current session, it would've been stored in the Store.
  const pendingBaAssessmentId = useSelector(state =>
    getPendingAssessmentIdByUserSlug(state, {
      userGuid: user?.piUserGuid,
    })
  );

  const createAssement = async () => {
    // Create a BA assessment for the survey User
    return await dispatch(
      createBehavioralAssessment({
        companyGuid: organization.pi_company_guid,
        userGuid: user.piUserGuid,
        payload: isTakingBaForSelf
          ? {
              email: loggedInUser.email,
              notifyAssessmentTakerCompletionEmail: true,
              notifyAnalystCompletionEmail: false,
              invitationSource: InvitationSource[InvitationSource.Perform], // string value of the enum
            }
          : {
              email: user.email,
              notifyAssessmentTakerCompletionEmail: true,
              notifyAnalystCompletionEmail: true,
              invitationSource: InvitationSource[InvitationSource.Perform], // string value of the enum
            },
      })
    );
  };

  // Check if user has assessmentUserId and assessmentId to re-use the existing assessment
  const getAssessmentId = async () => {
    // Check if there's already an assessment created for this user
    if (baProfile?.assessmentUserId && baProfile?.assessmentId && baProfile?.assessmentId !== InvalidAssessmentId) {
      return baProfile.assessmentId;
    } else {
      const response = await createAssement();
      return response.data.assessmentId;
    }
  };

  const getBaUrl = async () => {
    toggleIsCreatingBaLink();
    const url = app.behavioralAssessmentUrl;
    const assessmentId = pendingBaAssessmentId || (await getAssessmentId());
    toggleIsCreatingBaLink();
    return `${url}/v1/${organization.pi_company_guid}/${assessmentId}?type=emailba&p=hh`;
  };

  const sendBaEmail = async () => {
    toggleIsCreatingBaLink();
    await getAssessmentId();
    //TODO: Add an action item by creating a BaUrl using the assessmentId
    toggleIsCreatingBaLink();
  };

  const resendBaEmail = async () => {
    toggleIsCreatingBaLink();
    await createAssement();
    toggleIsCreatingBaLink();
  };

  return { sendBaEmail, resendBaEmail, getBaUrl, isCreatingBaLink };
};
